import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { organizationsApi, affiliatesApi, commissionsApi } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { toast } from 'react-hot-toast';
import {
  UserGroupIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ChartBarIcon,
  MegaphoneIcon,
  Cog6ToothIcon,
  PlusIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

interface OrgStats {
  totalAffiliates: number;
  activeAffiliates: number;
  pendingAffiliates: number;
  totalClicks: number;
  totalConversions: number;
  totalCommissions: number;
  pendingCommissions: number;
  approvedCommissions: number;
  totalPaidOut: number;
  totalSalesVolume: number;
}

interface TopAffiliate {
  affiliateId: string;
  referralCode: string;
  totalEarnings: number;
  totalConversions: number;
}

interface RecentCommission {
  commissionId: string;
  affiliateId: string;
  affiliateName?: string;
  commissionAmount: number;
  orderAmount: number;
  status: string;
  createdAt: string;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

function getInitials(id: string): string {
  return id.slice(0, 2).toUpperCase();
}

function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return 'Just now';
}

export default function OrgDashboard() {
  const { currentOrganization } = useAuthStore();
  const [stats, setStats] = useState<OrgStats | null>(null);
  const [topAffiliates, setTopAffiliates] = useState<TopAffiliate[]>([]);
  const [recentCommissions, setRecentCommissions] = useState<RecentCommission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportReport = async () => {
    if (!currentOrganization) return;

    setIsExporting(true);
    try {
      // Fetch all affiliates and commissions for the report
      const [affiliatesResponse, commissionsResponse] = await Promise.all([
        affiliatesApi.list({ limit: 1000 }),
        commissionsApi.list({ limit: 1000 }),
      ]);

      const affiliates = affiliatesResponse.data?.data?.affiliates ?? [];
      const commissions = commissionsResponse.data?.data?.commissions ?? [];

      // Create affiliate stats map
      const affiliateStats = new Map<string, {
        totalCommissions: number;
        pendingCommissions: number;
        approvedCommissions: number;
        paidCommissions: number;
      }>();

      commissions.forEach((c: { affiliateId: string; amount: number; status: string }) => {
        const existing = affiliateStats.get(c.affiliateId) || {
          totalCommissions: 0,
          pendingCommissions: 0,
          approvedCommissions: 0,
          paidCommissions: 0,
        };
        existing.totalCommissions += c.amount;
        if (c.status === 'pending') existing.pendingCommissions += c.amount;
        if (c.status === 'approved') existing.approvedCommissions += c.amount;
        if (c.status === 'paid') existing.paidCommissions += c.amount;
        affiliateStats.set(c.affiliateId, existing);
      });

      // Generate CSV content
      const headers = [
        'Affiliate ID',
        'Name',
        'Email',
        'Referral Code',
        'Status',
        'Total Earnings ($)',
        'Total Conversions',
        'Pending Commissions ($)',
        'Approved Commissions ($)',
        'Paid Commissions ($)',
        'Joined Date',
      ];

      const rows = affiliates.map((affiliate: {
        affiliateId: string;
        name?: string;
        email?: string;
        referralCode: string;
        status: string;
        totalEarnings: number;
        totalConversions: number;
        createdAt: string;
      }) => {
        const commStats = affiliateStats.get(affiliate.affiliateId) || {
          totalCommissions: 0,
          pendingCommissions: 0,
          approvedCommissions: 0,
          paidCommissions: 0,
        };
        return [
          affiliate.affiliateId,
          affiliate.name || 'N/A',
          affiliate.email || 'N/A',
          affiliate.referralCode,
          affiliate.status,
          (affiliate.totalEarnings / 100).toFixed(2),
          affiliate.totalConversions,
          (commStats.pendingCommissions / 100).toFixed(2),
          (commStats.approvedCommissions / 100).toFixed(2),
          (commStats.paidCommissions / 100).toFixed(2),
          new Date(affiliate.createdAt).toLocaleDateString(),
        ];
      });

      const csvContent = [
        headers.join(','),
        ...rows.map((row: (string | number)[]) => row.map((cell: string | number) => `"${cell}"`).join(',')),
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `affiliate-report-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Failed to export report:', error);
      toast.error('Failed to export report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!currentOrganization) return;

      try {
        const [statsResponse, affiliatesResponse, commissionsResponse] = await Promise.all([
          organizationsApi.getStats(currentOrganization.organizationId),
          affiliatesApi.list({ status: 'approved', limit: 5 }).catch(() => ({ data: { success: false, data: { affiliates: [] } } })),
          commissionsApi.list({ limit: 5 }).catch(() => ({ data: { success: false, data: { commissions: [] } } })),
        ]);

        if (statsResponse.data?.success) {
          setStats(statsResponse.data?.data?.stats ?? null);
        }

        if (affiliatesResponse.data?.success) {
          setTopAffiliates((affiliatesResponse.data?.data?.affiliates ?? []).slice(0, 5));
        }

        if (commissionsResponse.data?.success) {
          setRecentCommissions((commissionsResponse.data?.data?.commissions ?? []).slice(0, 5));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentOrganization]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rust"></div>
      </div>
    );
  }

  const statCards = [
    {
      name: 'Active Affiliates',
      value: formatNumber(stats?.activeAffiliates || 0),
      subtext: `${stats?.pendingAffiliates || 0} pending`,
      accentColor: 'bg-sage',
    },
    {
      name: 'Total Sales',
      value: formatCurrency(stats?.totalSalesVolume || 0),
      subtext: 'All time',
      accentColor: 'bg-rust',
    },
    {
      name: 'Commissions Owed',
      value: formatCurrency((stats?.pendingCommissions || 0) + (stats?.approvedCommissions || 0)),
      subtext: `${formatCurrency(stats?.pendingCommissions || 0)} pending`,
      accentColor: 'bg-tan',
    },
    {
      name: 'Total Paid Out',
      value: formatCurrency(stats?.totalPaidOut || 0),
      subtext: 'All time',
      accentColor: 'bg-charcoal',
    },
  ];

  const statusColors: Record<string, string> = {
    pending: 'bg-tan/30 text-tan-dark',
    approved: 'bg-sage/15 text-sage',
    paid: 'bg-sage/15 text-sage',
    rejected: 'bg-rust/10 text-rust',
  };

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-charcoal">Dashboard</h1>
          <p className="text-charcoal/60">Welcome back! Here's what's happening with your referral program.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExportReport}
            disabled={isExporting}
            className="btn btn-secondary"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            {isExporting ? 'Exporting...' : 'Export Report'}
          </button>
          <Link to="/org/affiliates?invite=true" className="btn btn-primary">
            <PlusIcon className="w-4 h-4 mr-2" />
            Invite Affiliate
          </Link>
        </div>
      </div>

      {/* Stats Grid with accent bars */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div
            key={stat.name}
            className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden"
          >
            {/* Left accent bar */}
            <div className={`absolute top-0 left-0 w-1 h-full ${stat.accentColor}`} />
            <div className="pl-2">
              <p className="text-sm text-charcoal/60 mb-1">{stat.name}</p>
              <p className="font-serif text-3xl text-charcoal leading-none mb-1">{stat.value}</p>
              <p className="text-xs text-charcoal/50">{stat.subtext}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Two column grid - Chart and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Chart */}
        <div className="lg:col-span-2 rounded-xl bg-warm-white shadow-sm">
          <div className="px-6 py-4 border-b border-tan/20 flex items-center justify-between">
            <h3 className="font-display text-lg font-bold text-charcoal">Referral Performance</h3>
            <Link to="/org/commissions" className="text-sm text-rust font-medium hover:underline">
              View Details →
            </Link>
          </div>
          <div className="p-6">
            {/* Performance Summary */}
            <div className="h-56 rounded-lg flex flex-col items-center justify-center bg-cream/30">
              <ChartBarIcon className="w-12 h-12 text-charcoal/20 mb-3" />
              <p className="text-charcoal/50 text-sm mb-4">Performance analytics coming soon</p>
              <div className="grid grid-cols-2 gap-6 text-center">
                <div>
                  <p className="font-serif text-2xl text-sage">{stats?.totalConversions || 0}</p>
                  <p className="text-xs text-charcoal/50">Total Conversions</p>
                </div>
                <div>
                  <p className="font-serif text-2xl text-sage">{formatCurrency(stats?.totalSalesVolume || 0)}</p>
                  <p className="text-xs text-charcoal/50">Total Sales</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Affiliates Leaderboard */}
        <div className="rounded-xl bg-warm-white shadow-sm">
          <div className="px-6 py-4 border-b border-tan/20 flex items-center justify-between">
            <h3 className="font-display text-lg font-bold text-charcoal">Top Affiliates</h3>
            <Link to="/org/affiliates" className="text-sm text-rust font-medium hover:underline">
              View All →
            </Link>
          </div>
          <div className="p-4">
            {topAffiliates.length > 0 ? (
              <ul className="space-y-1">
                {topAffiliates.map((affiliate, index) => (
                  <li
                    key={affiliate.affiliateId}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-cream/50 transition-colors"
                  >
                    {/* Rank */}
                    <span
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0
                          ? 'bg-rust text-white'
                          : index === 1
                          ? 'bg-sage text-white'
                          : index === 2
                          ? 'bg-tan text-charcoal'
                          : 'bg-cream text-charcoal'
                      }`}
                    >
                      {index + 1}
                    </span>
                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-full bg-charcoal flex items-center justify-center text-warm-white text-xs font-semibold">
                      {getInitials(affiliate.affiliateId)}
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-charcoal truncate">
                        {affiliate.referralCode}
                      </p>
                      <p className="text-xs text-charcoal/50">
                        {affiliate.totalConversions} referrals
                      </p>
                    </div>
                    {/* Earnings */}
                    <span className="font-serif text-lg text-sage">
                      {formatCurrency(affiliate.totalEarnings)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-center text-charcoal/50 py-8">No affiliates yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity Table */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/20 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-charcoal">Recent Activity</h3>
          <Link to="/org/commissions" className="text-sm text-rust font-medium hover:underline">
            View All →
          </Link>
        </div>
        {recentCommissions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">
                    Affiliate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">
                    Commission
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tan/10">
                {recentCommissions.map((commission) => (
                  <tr key={commission.commissionId} className="hover:bg-cream/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-tan flex items-center justify-center text-charcoal text-xs font-semibold">
                          {getInitials(commission.affiliateName || commission.affiliateId)}
                        </div>
                        <span className="text-sm text-charcoal">
                          {commission.affiliateName || `#${commission.affiliateId.slice(0, 8)}`}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                          statusColors[commission.status] || 'bg-cream text-charcoal'
                        }`}
                      >
                        {commission.status.charAt(0).toUpperCase() + commission.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-charcoal/60">
                      {getTimeAgo(commission.createdAt)}
                    </td>
                    <td className="px-6 py-4 font-medium text-charcoal">
                      {formatCurrency(commission.orderAmount)}
                    </td>
                    <td className="px-6 py-4 font-medium text-charcoal">
                      {formatCurrency(commission.commissionAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-charcoal/50 py-12">No recent activity</p>
        )}
      </div>

      {/* Quick Actions */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/20">
          <h3 className="font-display text-lg font-bold text-charcoal">Quick Actions</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/org/affiliates"
              className="flex items-center gap-4 p-4 bg-cream rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-10 h-10 bg-rust/10 rounded-lg flex items-center justify-center">
                <UserGroupIcon className="w-5 h-5 text-rust" />
              </div>
              <div>
                <p className="font-medium text-charcoal">Manage Affiliates</p>
                <p className="text-sm text-charcoal/60">Review and approve affiliates</p>
              </div>
            </Link>

            <Link
              to="/org/campaigns"
              className="flex items-center gap-4 p-4 bg-cream rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
                <MegaphoneIcon className="w-5 h-5 text-sage" />
              </div>
              <div>
                <p className="font-medium text-charcoal">Campaigns</p>
                <p className="text-sm text-charcoal/60">Create marketing campaigns</p>
              </div>
            </Link>

            <Link
              to="/org/commissions"
              className="flex items-center gap-4 p-4 bg-cream rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-10 h-10 bg-tan/30 rounded-lg flex items-center justify-center">
                <CurrencyDollarIcon className="w-5 h-5 text-charcoal" />
              </div>
              <div>
                <p className="font-medium text-charcoal">Review Commissions</p>
                <p className="text-sm text-charcoal/60">Approve pending commissions</p>
              </div>
            </Link>

            <Link
              to="/org/payouts"
              className="flex items-center gap-4 p-4 bg-cream rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
                <BanknotesIcon className="w-5 h-5 text-sage" />
              </div>
              <div>
                <p className="font-medium text-charcoal">Process Payouts</p>
                <p className="text-sm text-charcoal/60">Pay your affiliates</p>
              </div>
            </Link>

            <Link
              to="/org/settings"
              className="flex items-center gap-4 p-4 bg-cream rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-10 h-10 bg-charcoal/10 rounded-lg flex items-center justify-center">
                <Cog6ToothIcon className="w-5 h-5 text-charcoal" />
              </div>
              <div>
                <p className="font-medium text-charcoal">Settings</p>
                <p className="text-sm text-charcoal/60">Configure your program</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
